import type {
  GetInitData,
  IKitType,
  kitDetails,
} from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import type {
  PatientContentResponse,
  PatientContextEntry,
  PatientKitDetails,
} from '@viome/viome-base/src/usecase/PatientContext/PatientContext.model';
import type { BootupStatus } from '@/hooks/useBootup';

/**
 * Normalize a getInit-shaped payload (v1, v2, or a synthetic patient payload)
 * into the app's `BootupStatus`. Mirrors the original `useBootup` mapping so
 * downstream consumers behave identically.
 */
export const buildBootupStatus = (data: GetInitData): BootupStatus => {
  const {
    lockstatus,
    upsellMetaData,
    kitDetails,
    CDkitDetails,
    is_eligible_for_supplements,
    subscription_id: subscriptionId,
    enable_new_questionnaire,
    isATSUser,
    componentizationEligibleSkus,
  } = data;

  const status = {
    supplementRecsExists: lockstatus?.hasSupplementRecs ?? false,
    recommendationIsLockedByStudyExists:
      lockstatus?.recommendationIsLockedByStudy ?? false,
    recommendationExists: lockstatus?.hasRecommendation ?? false,
    hasPersonalizedSupplementRecommendationsExists:
      upsellMetaData?.hasPersonalizedSupplementRecommendations ?? false,
    kitType: kitDetails?.kitType,
    isEligibleForSupplements: is_eligible_for_supplements,
    rnrLastGeneratedDate: kitDetails?.lastRnRGeneratedDate,
    kitDetailsData: kitDetails,
    CDkitDetailsData: CDkitDetails,
    preRnRKitType: kitDetails?.pre_rnr_kit_type,
    subscriptionId,
    enableNewQuestionnaire: enable_new_questionnaire || false,
    isATSUser: isATSUser ?? false,
    componentizationEligibleSkus: componentizationEligibleSkus ?? null,
  };

  return status as BootupStatus;
};

const mapPatientKit = (k: PatientKitDetails | null): kitDetails | null =>
  k
    ? {
        kitId: k.kitId,
        kitType: k.kitType as IKitType,
        lastRnRGeneratedDate: k.lastRnRGeneratedDate ?? '',
        kitReIssued: k.kitReIssued,
        PreRnrKitId: k.PreRnrKitId,
        pre_rnr_kit_type: k.pre_rnr_kit_type as IKitType,
      }
    : null;

/**
 * Build a getInit-shaped payload for a selected patient org: kit + lockstatus
 * + flags from the content entry, account-wide extras (upsell / ATS /
 * subscription) from the stored getInit v2 payload. `enable_new_questionnaire`
 * is per-kit on the content entry; fall back to the consumer v2 value.
 */
export const buildPatientGetInit = (
  entry: PatientContextEntry,
  content: PatientContentResponse,
  accountExtras: GetInitData | null,
): GetInitData => ({
  isStudyEnabled: content.isStudyEnabled,
  isVegetarian: content.isVegetarian,
  is_eligible_for_supplements: content.is_eligible_for_supplements,
  enable_new_questionnaire:
    entry.kitDetails?.enable_new_questionnaire ??
    accountExtras?.enable_new_questionnaire ??
    false,
  kitDetails: mapPatientKit(entry.kitDetails),
  CDkitDetails: mapPatientKit(entry.CDkitDetails),
  lockstatus: entry.lockstatus,
  upsellMetaData: accountExtras?.upsellMetaData ?? {
    hasPersonalizedSupplementRecommendations: false,
  },
  client_config: content.client_config,
  subscription_id: accountExtras?.subscription_id ?? null,
  isATSUser: accountExtras?.isATSUser ?? false,
  componentizationEligibleSkus:
    accountExtras?.componentizationEligibleSkus ?? null,
});
